/**
 * OrderChangeResolution — the BLU-695 order-change RESOLVER (plan + actions).
 *
 * One component, one behaviour; the CONTAINER decides where it lives. The
 * inline card (1 to 2 changed SKUs) and `OrderChangeSheet` (3+) both render
 * this, and the only thing that differs is where the confirm step appears:
 *
 *   confirmMode='dialog'  inline on the page — a page-level AlertDialog is
 *                         allowed (DESIGN.md §10 bans dialog-INSIDE-dialog,
 *                         not dialog-from-page).
 *   confirmMode='footer'  inside the Sheet — the confirm is a second STATE of
 *                         the footer (hand-over locked decision 5). Never an
 *                         AlertDialog over the Sheet: that is the stacked-modal
 *                         trap DESIGN.md §10 / PRODUCTION_DESIGN.md §6.1 ban,
 *                         and swapping the footer keeps the plan visible behind
 *                         the question being asked.
 *
 * LIVE COVERAGE, NEVER THE SNAPSHOT (locked decision 1). Every number here comes
 * from `getOrderCoverage`, which runs the same `evaluateOrderCoverage` the two
 * mutations re-run server-side. The `prod_issues.metadata` counts the browse row
 * shows are up to a cron cycle stale; quoting them here would let the card state
 * a plan its own Apply contradicts.
 *
 * DESIGN LANE: the Issues board is a hybrid — inventory-app chrome, operator
 * action lanes at `h-12` (`OrderResolutionFlow` is the sibling precedent). The
 * browse rows stay at inventory density; EVERY control in this component is
 * `h-12`. One purple CTA ("Fix this order"); everything else is outline or
 * ghost. Colour is never the only signal: each plan row carries a glyph AND its
 * own verb.
 *
 * CONFIRM POLICY: the confirm step fires only when the plan CANCELS something.
 * A pure top-up mints extra units for a customer who asked for them (additive,
 * and the server caps it at the freshly recomputed deficit), so gating the 85%
 * case behind a second click is friction, not safety. Cancelling a garment ends
 * physical work, so it confirms AND demands a typed reason.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MinusCircle,
  PlusCircle,
  RefreshCw,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getUserMessage } from '@/lib/trpc-error-utils';
import type { RouterOutputs } from '@/lib/trpc';
import {
  useCancelSurplusUnits,
  useOrderCoverage,
  useAcceptOrderChanges,
  useTopUpOrderCoverage,
} from '../hooks/useProductionIssues';
import {
  buildReasonText,
  nonActionableCopy,
  planLineDetail,
  planRowVerb,
  planSummaryText,
  REASON_MAX_LENGTH,
  type Translate,
} from '../orderChangeWording';

type OrderCoverage = RouterOutputs['productionOrders']['issues']['getOrderCoverage'];
type CoverageLine = OrderCoverage['lines'][number];

/**
 * Orders with this many changed base SKUs (or more) open in the side Sheet;
 * fewer expand the card in place (hand-over locked decision 4 — roughly 85% of
 * changes touch a single SKU, so the panel is the overflow, not the default).
 */
export const SHEET_SKU_THRESHOLD = 3;

/** Where an order's resolution surface should render. Pure — unit-tested. */
export function resolutionPresentation(changedSkuCount: number): 'inline' | 'sheet' {
  return changedSkuCount >= SHEET_SKU_THRESHOLD ? 'sheet' : 'inline';
}

/** `cancelSurplusUnits` accepts at most 200 ids per call (backend zod schema). */
const MAX_CANCEL_PER_CALL = 200;

/** The verdicts that have something to apply. Everything else is reported honestly. */
const ACTIONABLE_VERDICTS = new Set<OrderCoverage['verdict']>(['under', 'over', 'both']);

/**
 * BLU-695 §2b outcome, reported up via `onBinMove` — the resolver no longer
 * holds this as local state (it would unmount with the card on refetch); the
 * LIST owns a durable banner keyed by prodOrderId instead (OrderChangeIssuesList).
 * `binLabel: null` means the claim failed — one shape, no discriminated union.
 */
export type BinMoveResult = { orderLabel: string; binLabel: string | null };

export interface OrderChangeResolutionProps {
  prodOrderId: string;
  /** The order's display name, e.g. "#1049479" — carried on `BinMoveResult`
   *  so the container never has to re-attach it after the fact. */
  orderLabel: string;
  /**
   * Every OPEN ORDER_CHANGED issue row for this order. "Accept as-is" resolves
   * all of them: `resolveIssue`'s sibling cascade deliberately EXEMPTS
   * ORDER_CHANGED, so resolving one row would leave the others blocking.
   */
  openIssueIds: string[];
  confirmMode: 'dialog' | 'footer';
  /** Fired after any successful mutation — the container closes/collapses.
   *  Fires unconditionally now, including when `onBinMove` also fires: the
   *  bin-move banner lives at the LIST level and outlives this panel. */
  onResolved?: () => void;
  /** BLU-695 §2b — reports a bin claim/failure so the container can render a
   *  durable, list-level banner (survives this panel collapsing/unmounting). */
  onBinMove?: (result: BinMoveResult) => void;
}

export function OrderChangeResolution({
  prodOrderId,
  orderLabel,
  openIssueIds,
  confirmMode,
  onResolved,
  onBinMove,
}: OrderChangeResolutionProps) {
  const { t } = useTranslation('production');
  // The plain translator the shared wording helpers take (orderChangeWording.ts).
  const translate: Translate = (key, params) => t(key, params);
  const isSheet = confirmMode === 'footer';
  const reasonId = useId();

  const coverageQuery = useOrderCoverage(prodOrderId);
  const coverage = coverageQuery.data;

  const topUp = useTopUpOrderCoverage();
  const cancelSurplus = useCancelSurplusUnits();
  const acceptOrderChanges = useAcceptOrderChanges();

  // Two reasons, deliberately separate. "Why are these garments being
  // cancelled" and "why is this mismatch acceptable" are different questions
  // with different audiences (the floor reads the first off the cancelled
  // garment; the audit trail reads the second), so one must never pre-fill the
  // other.
  const [cancelReason, setCancelReason] = useState('');
  const [acceptReason, setAcceptReason] = useState('');
  const [stage, setStage] = useState<'plan' | 'confirmFix' | 'confirmAccept'>('plan');
  /** Sticky, in-surface failure text. A toast alone would vanish under the fold. */
  const [failure, setFailure] = useState<string | null>(null);

  const lines: CoverageLine[] = coverage?.lines ?? [];
  const cancelLines = lines.filter((l) => l.over > 0);
  const makeLines = lines.filter((l) => l.under > 0);
  const surplusUnitIds = cancelLines.flatMap((l) => l.surplusUnitIds).slice(0, MAX_CANCEL_PER_CALL);
  const totalUnder = coverage?.totalUnder ?? 0;
  const totalOver = surplusUnitIds.length;

  const isActionable = !!coverage && ACTIONABLE_VERDICTS.has(coverage.verdict);
  const needsReason = totalOver > 0;
  const cancelReasonText = cancelReason.trim();
  const acceptReasonText = acceptReason.trim();
  const canFix = isActionable && (!needsReason || cancelReasonText.length > 0);
  const canAccept = acceptReasonText.length > 0;
  const isBusy = topUp.isPending || cancelSurplus.isPending || acceptOrderChanges.isPending;

  /**
   * Plain-words statement of what actually changed, seeded into BOTH reason
   * fields so the coordinator doesn't retype what the system already knows.
   * This doesn't violate the "one must never pre-fill the other" rule above:
   * both seeds come from live coverage, not from each other, and each stays
   * independently editable. Seeding only fills an empty field and re-fires
   * only if the text itself changes.
   *
   * One full sentence per changed line, by garment name, one per row of the
   * box, and never longer than the server accepts (`buildReasonText`).
   */
  const changeSummary = buildReasonText(lines, translate);

  useEffect(() => {
    if (!changeSummary) return;
    setCancelReason((prev) => (prev.trim() === '' ? changeSummary : prev));
    setAcceptReason((prev) => (prev.trim() === '' ? changeSummary : prev));
  }, [changeSummary]);

  // ---- Actions ------------------------------------------------------------

  /**
   * Cancel first, then top up. Order matters: the cancel frees the surplus
   * before the mint recomputes the deficit, so a swap never mints against units
   * it is about to destroy. Each half reports its OWN outcome — a cancel that
   * lands followed by a top-up that fails is a partial, never a green toast.
   */
  const handleFix = async () => {
    if (!canFix || isBusy) return;
    setFailure(null);

    let cancelled = 0;
    if (totalOver > 0) {
      try {
        const result = await cancelSurplus.mutateAsync({
          prod_order_id: prodOrderId,
          garment_unit_ids: surplusUnitIds,
          reason: cancelReasonText,
        });
        cancelled = result.cancelled;
        // The backend cancels per-unit and reports failures instead of
        // throwing mid-loop — "cancelled N of M" must never read as
        // "nothing was changed". Optional-read: a deploy-window server may
        // predate the field.
        const failedUnits = result.failed ?? [];
        if (failedUnits.length > 0) {
          const message = t('issues.orderChanges.fixCancelPartial', {
            cancelled: result.cancelled,
            total: result.cancelled + failedUnits.length,
            error: failedUnits[0]?.error ?? '',
          });
          setFailure(message);
          setStage('plan');
          toast.error(t('issues.orderChanges.fixPartialTitle'), {
            description: message,
            duration: Infinity,
          });
          return;
        }
      } catch (error) {
        // Nothing changed — say exactly that, and stay put so the plan is
        // still on screen when the operator retries.
        const message = t('issues.orderChanges.fixCancelFailed', {
          error: getUserMessage(error),
        });
        setFailure(message);
        setStage('plan');
        toast.error(t('issues.orderChanges.fixFailedTitle'), {
          description: message,
          duration: Infinity,
        });
        return;
      }
    }

    let minted = 0;
    let claimedBinLabel: string | null = null;
    let binClaimFailed = false;
    if (totalUnder > 0) {
      try {
        const result = await topUp.mutateAsync({ prod_order_id: prodOrderId });
        minted = result.minted;
        claimedBinLabel = result.claimedBin?.binLabel ?? null;
        binClaimFailed = result.binClaimFailed ?? false;
      } catch (error) {
        // "Half the fix landed" is only true when this run actually cancelled
        // something before the top-up failed. A refused top-up with nothing
        // cancelled (a pure add, e.g. the shelf-garment refusal) changed
        // nothing at all, and says so.
        const halfLanded = cancelled > 0;
        const message = halfLanded
          ? t('issues.orderChanges.fixPartial', { cancelled, error: getUserMessage(error) })
          : t('issues.orderChanges.fixCancelFailed', { error: getUserMessage(error) });
        setFailure(message);
        setStage('plan');
        toast.error(
          t(halfLanded ? 'issues.orderChanges.fixPartialTitle' : 'issues.orderChanges.fixFailedTitle'),
          { description: message, duration: Infinity },
        );
        return;
      }
    }

    setStage('plan');
    setCancelReason('');
    const doneMessage =
      cancelled > 0 && minted > 0
        ? t('issues.orderChanges.fixDoneBoth', { cancelled, minted })
        : cancelled > 0
          ? t('issues.orderChanges.fixDoneCancelled', { count: cancelled })
          : minted > 0
            ? t('issues.orderChanges.fixDoneQueued', { count: minted })
            : t('issues.orderChanges.fixDoneNothing');
    if (claimedBinLabel) {
      // Redundant channel, cheap: the durable list-level banner (rendered by
      // the container via `onBinMove`) is the operator's real instruction —
      // this just echoes the bin label for anyone glancing at the toast.
      toast.success(doneMessage, {
        description: t('issues.orderChanges.binClaimedToastSuffix', { label: claimedBinLabel }),
      });
      onBinMove?.({ orderLabel, binLabel: claimedBinLabel });
    } else {
      toast.success(doneMessage);
      // No separate toast for a failed claim — the list-level banner
      // (`onBinMove`) already owns this message, with the order label
      // interpolated; a bare toast here has nothing to interpolate it with.
      if (binClaimFailed) {
        onBinMove?.({ orderLabel, binLabel: null });
      }
    }
    onResolved?.();
  };

  /**
   * Clear the blocks without producing or cancelling anything. ORDER-scoped on
   * the server: the browse list is paginated, so `openIssueIds` (derived from
   * the current page's group) can be a SUBSET of the order's open rows —
   * accepting only that subset would report success while off-page rows keep
   * blocking. The server resolves every open ORDER_CHANGED row for the order
   * and reports honest {done, failed} counts.
   */
  const handleAccept = async () => {
    if (!canAccept || isBusy || openIssueIds.length === 0) return;
    setFailure(null);

    let result: { done: number; failed: Array<{ issueId: string; error: string }> };
    try {
      result = await acceptOrderChanges.mutateAsync({
        prod_order_id: prodOrderId,
        notes: acceptReasonText,
      });
    } catch (error) {
      const message = t('issues.orderChanges.acceptFailed', { error: getUserMessage(error) });
      setFailure(message);
      setStage('plan');
      toast.error(t('issues.orderChanges.acceptFailedTitle'), {
        description: message,
        duration: Infinity,
      });
      return;
    }

    if (result.failed.length > 0) {
      const message =
        result.done > 0
          ? t('issues.orderChanges.acceptPartial', {
              done: result.done,
              total: result.done + result.failed.length,
              error: result.failed[0]?.error ?? '',
            })
          : t('issues.orderChanges.acceptFailed', { error: result.failed[0]?.error ?? '' });
      setFailure(message);
      setStage('plan');
      toast.error(t('issues.orderChanges.acceptFailedTitle'), {
        description: message,
        duration: Infinity,
      });
      return;
    }

    setStage('plan');
    setAcceptReason('');
    toast.success(t('issues.orderChanges.acceptDone', { count: result.done }));
    onResolved?.();
  };

  // ---- Loading / error ----------------------------------------------------

  if (coverageQuery.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-14 w-full rounded-md" />
        <Skeleton className="h-14 w-full rounded-md" />
      </div>
    );
  }

  if (coverageQuery.isError || !coverage) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
        <p className="flex items-center gap-2 text-sm font-medium text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('issues.orderChanges.coverageLoadFailed')}
        </p>
        <Button
          variant="outline"
          className="h-12 gap-1.5"
          onClick={() => coverageQuery.refetch()}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t('issues.orderChanges.coverageRetry')}
        </Button>
      </div>
    );
  }

  // ---- Shared fragments ---------------------------------------------------

  const acceptButton = (
    <Button
      variant="ghost"
      className="h-12 gap-1.5"
      disabled={isBusy || openIssueIds.length === 0}
      onClick={() => setStage('confirmAccept')}
    >
      {t('issues.orderChanges.acceptAsIs')}
    </Button>
  );

  const confirmFixBody = (
    <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{t('issues.orderChanges.confirmFixBody')}</span>
    </div>
  );

  /** The reason field only. The explainer is rendered by each container so the
   *  dialog can put it in its `AlertDialogDescription` instead of announcing the
   *  same sentence twice. */
  const acceptReasonField = (
    <div className="space-y-2">
      <Label htmlFor={`${reasonId}-accept`} className="text-sm font-medium">
        {t('issues.orderChanges.acceptReasonLabel')}
        <span className="ml-1 text-destructive">*</span>
      </Label>
      <Textarea
        id={`${reasonId}-accept`}
        value={acceptReason}
        onChange={(e) => setAcceptReason(e.target.value)}
        maxLength={REASON_MAX_LENGTH}
        rows={3}
        className="min-h-12 max-w-prose"
        placeholder={t('issues.orderChanges.acceptReasonPlaceholder')}
      />
    </div>
  );

  // By name when the plan is one garment each way and both have a stored name
  // ("Cancel Baby Tee, Black, S. Make Baby Tee, Black, L."); otherwise the
  // count summary, and the plan rows above carry the names.
  const countPlanSummary = (): string => {
    if (totalOver > 0 && totalUnder > 0) {
      return t('issues.orderChanges.planSummaryBoth', { cancel: totalOver, make: totalUnder });
    }
    if (totalOver > 0) return t('issues.orderChanges.planSummaryCancel', { count: totalOver });
    return t('issues.orderChanges.planSummaryMake', { count: totalUnder });
  };
  const planSummary =
    planSummaryText(cancelLines, makeLines, totalOver, translate) ?? countPlanSummary();

  const renderPlanRow = (tone: 'cancel' | 'make', line: CoverageLine) => {
    const namedVerb = planRowVerb(tone, line, translate);
    return (
      <PlanRow
        key={`${tone}-${line.orderItemId}`}
        tone={tone}
        verb={
          namedVerb ??
          (tone === 'cancel'
            ? t('issues.orderChanges.planCancel', { count: line.over })
            : t('issues.orderChanges.planMake', { count: line.under }))
        }
        sku={line.sku || t('issues.orderChanges.planUnknownSku')}
        skuSecondary={namedVerb !== null}
        detail={planLineDetail(line, Boolean(coverage?.fulfilmentOrderGid), translate)}
      />
    );
  };

  // ---- Body ---------------------------------------------------------------

  const body = isActionable ? (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">{t('issues.orderChanges.planHeading')}</h3>
        <p className="text-xs text-muted-foreground">{t('issues.orderChanges.planLiveNote')}</p>
      </div>

      <ul className="divide-y divide-border overflow-hidden rounded-md border">
        {/* A row names the garment when the order line has a stored name, and
            the SKU steps back to secondary text. With no stored name the row is
            the count verb and the SKU, exactly as it was. */}
        {cancelLines.map((line) => renderPlanRow('cancel', line))}
        {makeLines.map((line) => renderPlanRow('make', line))}
      </ul>

      {/* Hidden while the Sheet's footer is asking its own question — the footer
          owns the input at that point, and two live textareas on one screen is
          two ways to answer one thing. */}
      {needsReason && !(isSheet && stage !== 'plan') && (
        <div className="space-y-1.5">
          <Label htmlFor={reasonId} className="text-sm font-medium">
            {t('issues.orderChanges.reasonLabel')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Textarea
            id={reasonId}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            maxLength={REASON_MAX_LENGTH}
            rows={3}
            className="min-h-12 max-w-prose"
            placeholder={t('issues.orderChanges.reasonPlaceholder')}
          />
          <p className="text-xs text-muted-foreground">{t('issues.orderChanges.reasonHint')}</p>
        </div>
      )}

      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}
    </div>
  ) : (
    <NonActionableState coverage={coverage} failure={failure} />
  );

  // ---- Footer -------------------------------------------------------------

  let footer: React.ReactNode;

  if (isSheet && stage === 'confirmFix') {
    // Locked decision 5: a SECOND STATE of the footer, not a layer on top.
    footer = (
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('issues.orderChanges.confirmFixTitle')}</p>
        {confirmFixBody}
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            className="h-12"
            disabled={isBusy}
            onClick={() => setStage('plan')}
          >
            {t('issues.orderChanges.back')}
          </Button>
          <Button
            className="h-12 gap-1.5 bg-threadheads-purple px-6 text-white hover:bg-threadheads-purple/90"
            disabled={isBusy}
            onClick={handleFix}
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Wrench className="h-4 w-4" aria-hidden="true" />
            )}
            {t('issues.orderChanges.confirmFixCta')}
          </Button>
        </div>
      </div>
    );
  } else if (isSheet && stage === 'confirmAccept') {
    footer = (
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('issues.orderChanges.acceptTitle')}</p>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('issues.orderChanges.acceptBody')}
        </p>
        {acceptReasonField}
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            className="h-12"
            disabled={isBusy}
            onClick={() => setStage('plan')}
          >
            {t('issues.orderChanges.back')}
          </Button>
          <Button
            variant="ghost"
            className="h-12 gap-1.5"
            disabled={isBusy || !canAccept}
            onClick={handleAccept}
          >
            {isBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('issues.orderChanges.acceptCta')}
          </Button>
        </div>
      </div>
    );
  } else if (isActionable) {
    // After a refused fix the plan still stands, but it is no longer a green
    // "ready to go": the failure text above says why it did not land.
    const planReady = canFix && !failure;
    footer = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'mr-auto flex items-center gap-2 text-sm font-medium',
            !canFix ? 'text-amber-700' : planReady ? 'text-emerald-700' : 'text-muted-foreground',
          )}
        >
          {planReady && <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {!canFix && <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span>{canFix ? planSummary : t('issues.orderChanges.statusNeedsReason')}</span>
        </div>
        {acceptButton}
        <Button
          className="h-12 gap-1.5 bg-threadheads-purple px-6 text-white hover:bg-threadheads-purple/90"
          disabled={!canFix || isBusy}
          onClick={() => (totalOver > 0 ? setStage('confirmFix') : handleFix())}
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Wrench className="h-4 w-4" aria-hidden="true" />
          )}
          {t('issues.orderChanges.fix')}
        </Button>
      </div>
    );
  } else {
    // Nothing to apply — refresh or accept, never a disabled purple CTA the
    // operator has to guess at.
    footer = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          className="h-12 gap-1.5"
          disabled={coverageQuery.isFetching || isBusy}
          onClick={() => coverageQuery.refetch()}
        >
          <RefreshCw
            className={cn('h-4 w-4', coverageQuery.isFetching && 'animate-spin')}
            aria-hidden="true"
          />
          {t('issues.orderChanges.refresh')}
        </Button>
        {acceptButton}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4', isSheet && 'min-h-0 flex-1')}>
      {/* BLU-695 §2b — the bin-move banner now renders at the LIST level
          (OrderChangeIssuesList's `pendingBinMoves`), not here: this panel
          can unmount on the OPEN-list refetch that follows a successful fix,
          which would kill a locally-held instruction before it's dismissed. */}
      <div className={cn(isSheet && 'min-h-0 flex-1 overflow-y-auto pr-1')}>{body}</div>
      <div className={cn('shrink-0', isSheet && 'border-t pt-4')}>{footer}</div>

      {/* Inline path only: a page-level AlertDialog is fine (DESIGN.md §10 bans
          dialog-inside-dialog). The Sheet path never mounts these. */}
      {!isSheet && (
        <>
          <AlertDialog
            open={stage === 'confirmFix'}
            onOpenChange={(open) => !open && setStage('plan')}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('issues.orderChanges.confirmFixTitle')}</AlertDialogTitle>
                <AlertDialogDescription>{planSummary}</AlertDialogDescription>
              </AlertDialogHeader>
              {confirmFixBody}
              <AlertDialogFooter>
                <AlertDialogCancel className="h-12" disabled={isBusy}>
                  {t('issues.orderChanges.back')}
                </AlertDialogCancel>
                <Button
                  className="h-12 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                  disabled={isBusy}
                  onClick={handleFix}
                >
                  {isBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Wrench className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t('issues.orderChanges.confirmFixCta')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog
            open={stage === 'confirmAccept'}
            onOpenChange={(open) => !open && setStage('plan')}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('issues.orderChanges.acceptTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('issues.orderChanges.acceptBody')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {acceptReasonField}
              <AlertDialogFooter>
                <AlertDialogCancel className="h-12" disabled={isBusy}>
                  {t('issues.orderChanges.back')}
                </AlertDialogCancel>
                <Button
                  variant="ghost"
                  className="h-12 gap-1.5"
                  disabled={isBusy || !canAccept}
                  onClick={handleAccept}
                >
                  {isBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {t('issues.orderChanges.acceptCta')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Plan row — glyph + verb + SKU. Colour is never the only signal.
// ============================================================================

function PlanRow({
  tone,
  verb,
  sku,
  skuSecondary = false,
  detail,
}: {
  tone: 'cancel' | 'make';
  verb: string;
  sku: string;
  /** The verb already names the garment, so the SKU is the floor's matching
   *  code beside it, not the row's identity: smaller and muted. */
  skuSecondary?: boolean;
  detail: string;
}) {
  const isCancel = tone === 'cancel';
  const Icon = isCancel ? MinusCircle : PlusCircle;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <Icon
        className={cn('h-4 w-4 shrink-0', isCancel ? 'text-red-600' : 'text-emerald-600')}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        {/* A product name can be long: it wraps, it is never truncated. */}
        <p
          className={cn(
            'break-words text-sm font-medium tabular-nums',
            isCancel ? 'text-red-700' : 'text-emerald-700',
          )}
        >
          {verb}
        </p>
        <p className="text-xs tabular-nums text-muted-foreground">{detail}</p>
      </div>
      <span
        className={cn(
          'shrink-0 font-mono',
          skuSecondary ? 'text-xs text-muted-foreground' : 'text-sm',
        )}
      >
        {sku}
      </span>
    </li>
  );
}

// ============================================================================
// Verdicts with nothing to apply. Each says WHY, in the operator's language.
// ============================================================================

function NonActionableState({
  coverage,
  failure,
}: {
  coverage: OrderCoverage;
  failure: string | null;
}) {
  const { t } = useTranslation('production');

  const copy = nonActionableCopy(coverage);
  const title = t(copy.titleKey);
  const description = t(copy.descriptionKey);
  const tone = copy.tone;

  return (
    <div className="space-y-2">
      <p
        className={cn(
          'flex items-center gap-2 text-sm font-medium',
          tone === 'ok' ? 'text-emerald-700' : 'text-foreground',
        )}
      >
        {tone === 'ok' ? (
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        )}
        {title}
      </p>
      <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}
    </div>
  );
}
